import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Head from "next/head";
import Link from "next/link";

import CosmicCollectionStars from "../../components/Common/CosmicCollectionStars/CosmicCollectionStars";

// One-line tagline per catalogue. Keep concise — shown directly under the hero title.
const sdata = {
  brightests_stars: {
    id: 1,
    slug: "brightests_stars",
    title: "Brightest Stars",
    description:
      "The brightest stars visible from Earth — visual magnitude, distance and stellar class for each entry.",
  },
  exceptional_asteroids: {
    id: 2,
    slug: "exceptional_asteroids",
    title: "Exceptional Asteroids",
    description:
      "Notable asteroids of scientific and historical significance from across the solar system.",
  },
  nearest_galaxies: {
    id: 3,
    slug: "nearest_galaxies",
    title: "Nearest Galaxies",
    description:
      "Galaxies in our cosmic neighborhood — members of the Local Group and beyond.",
  },
  prominent_constellations: {
    id: 4,
    slug: "prominent_constellations",
    title: "Prominent Constellations",
    description:
      "The iconic constellations of the night sky — their bright stars, deep-sky objects and mythology.",
  },
  nearest_moons: {
    id: 5,
    slug: "nearest_moons",
    title: "Nearest Moons",
    description:
      "Notable moons of the solar system — orbital data, surface features and exploration history.",
  },
  closest_minor_planets: {
    id: 6,
    slug: "closest_minor_planets",
    title: "Closest Minor Planets",
    description:
      "Minor planets and dwarf worlds nearest to Earth — orbits, sizes and discovery records.",
  },
  caldwell_catalogue: {
    id: 7,
    slug: "caldwell_catalogue",
    title: "Caldwell Catalogue",
    description:
      "Patrick Moore's deep-sky companion to the Messier list — 109 striking objects visible from both hemispheres.",
  },
  messier_catalogue: {
    id: 8,
    slug: "messier_catalogue",
    title: "Messier Catalogue",
    description:
      "Charles Messier's catalogue of nebulae, star clusters and galaxies — the original deep-sky observing list.",
  },
  solar_system: {
    id: 8,
    slug: "solar_system",
    title: "Solar System",
    description: "Bodies of our solar system — planets, moons and dwarf worlds.",
  },
};

export default function Category() {
  const router = useRouter();
  const { id } = router.query;
  const [staticData, setStaticData] = useState(null);

  useEffect(() => {
    if (id) setStaticData(sdata[id] || null);
  }, [id]);

  return (
    <>
      <Head>
        <title>{staticData ? `${staticData.title} — NiftySky` : "NiftySky"}</title>
      </Head>

      {/* Subtle cosmic backdrop — matches /real-estate */}
      <div
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background: "radial-gradient(ellipse at 50% 0%, #0d1f3c 0%, #09080D 60%)",
        }}
      />

      {/* Hero */}
      <section className="text-white w-full px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-10">
        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-gray-400">
          <Link href="/" className="hover:text-sky-300 transition-colors">
            Home
          </Link>
          <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sky-300/80 truncate max-w-[260px]">
            {staticData?.title || "Catalogue"}
          </span>
        </nav>

        <div className="text-center max-w-3xl mx-auto">
          <p className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-300 text-[11px] font-semibold uppercase tracking-[0.18em]">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
            NiftySky Catalogue
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sky-300 via-blue-400 to-blue-300 bg-clip-text text-transparent font-head mb-4 leading-tight">
            {staticData?.title || "Catalogue"}
          </h1>
          {staticData?.description && (
            <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
              {staticData.description}
            </p>
          )}
        </div>
      </section>

      <CosmicCollectionStars
        category={staticData?.id}
        sectionLabel={staticData?.title || "Items"}
      />
    </>
  );
}
